//#region types
import { CurrencyEnums, FormEnums } from "models/enums";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { EditableTableFieldProps, FormFieldDefinitionType } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TimeReportingProjectCategoryFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import MemberBillingDetailsField from "../../_partial/memberBillingDetailsField";
import { Grid } from "@mui/material";
//#endregion
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useCategoryMemberRatesFields(
  props: TimeReportingProjectCategoryFormViewProps
): FormFieldDefinitionType<TimeReportingProjectFormTypes.ProjectCategory> {
  const { project } = props;

  const getBillingModels = () => {
    const billingModels = EnumHelper.getNumericItemValues(ApiModel.TimeReportingPersonProjectBillingModel).map(
      (item) =>
        ({
          value: LocalizationHelperInstance.getTranslatedEnumValue("TimeReportingProject.BillingModel", item),
          data: item,
          id: item,
        } as AutocompleteListItem)
    );

    return billingModels;
  };

  return {
    memberRates: {
      label: "",
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) =>
        !values.categoryMembers?.some((cm) => cm.selected),
      innerFields: {
        selected: {
          label: "",
          type: FormEnums.FormFieldType.CHECKBOX,
          classes: {
            input: props.isNew ?? true ? cssExports.disable : "",
            formControl: cssExports.select,
          },
          disabled: (values: TimeReportingProjectFormTypes.MemberRate) => props.isNew ?? true,
        },
        name: {
          label: "",
          type: FormEnums.FormFieldType.TEXT,
          readonly: true,
          classes: { formControl: cssExports.truncateName },
        },
        individualBillingModelName: {
          label: " ",
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          widthColumnCount: 5,
          extraProps: {
            items: getBillingModels(),
            referenceFieldName: "individualBillingModel",
            onItemSelect: props.onIndividualBillingModelChange,
          },
          disabled: (values: TimeReportingProjectFormTypes.MemberRate) => !values.selected,
        },
        hourlyRate: {
          label: " ",
          type: FormEnums.FormFieldType.CURRENCY,
          rightAlign: true,
          extraProps: {
            currency: project?.currency ?? CurrencyEnums.CurrencyCode.SEK,
          },
          onChangeHandler: props.onHourlyRateChange,
          disabled: (values: TimeReportingProjectFormTypes.MemberRate) => !values.selected,
        },
        hours: {
          label: " ",
          type: FormEnums.FormFieldType.NUMBER,
          rightAlign: true,
          extraProps: { suffix: "h", decimalPoints: 2, hideValueIfReadOnly: true },
          readonly: (values: TimeReportingProjectFormTypes.MemberRate) => {
            return values.individualBillingModel === ApiModel.TimeReportingPersonProjectBillingModel.Hourly;
          },
          widthColumnCount: 2,
          onChangeHandler: props.onMemberRateHoursChange,
          disabled: (values: TimeReportingProjectFormTypes.MemberRate) => !values.selected,
        },
      },
      className: cssExports.categoryMemeberContainer,
      extraProps: {
        isCompactTable: true,
        selectionFieldName: "selected",
        hideActions: true,
        disableSelection: props.isNew ?? true,
        showColumnHeaders: true,
        headers: [
          {
            label: "",
            fieldName: "selected",
          },
          {
            fieldName: "name",
            label: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.ChangeAllMemberRates
            ),
            columnSpan: 4,
          },
        ],
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectCategory>,
    },
    changesApplyFrom: {
      label: TranslationKeys.TimeReportingProject.ChangesApplyFromTheFirstInTheMonth,
      controlLabel: " ",
      type: FormEnums.FormFieldType.MONTH,
      extraProps: {
        hideClearable: true,
        popperPositionFixed: true,
      },
      onChangeHandler: props.onChangesApplyMonthChange,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) =>
        props.isNew || !values.memberRates?.some((mr) => mr.selected),
    },
    futureMemberRates: {
      label: TranslationKeys.TimeReportingProject.QueuedRateChanges,
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) =>
        props.isNew || values.futureMemberRates === undefined || values.futureMemberRates.length === 0,
      innerFields: {
        name: {
          label: " ",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <MemberBillingDetailsField currency={props.project?.currency}></MemberBillingDetailsField>,
          readonly: true,
        },
        validFromName: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: (
            <Grid container justifyContent="flex-end">
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.ValidFrom)}
            </Grid>
          ),
          rightAlign: true,
          classes: { formControl: cssExports.validFromFieldContainer },
        },
        validFrom: {
          label: " ",
          type: FormEnums.FormFieldType.MONTH,
          readonly: true,
          extraProps: {
            popperPositionFixed: true,
            hideClearable: true,
          },
        },
      },
      extraProps: {
        isCompactTable: true,
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectCategory>,
    },
  };
}

export default useCategoryMemberRatesFields;
